import FeeScheduleCategoryCommand, {
  FeeScheduleCategoryCommandPayload,
} from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import { EntityMemberJSON } from "_shared/entity-members/entity-member-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

/**
 * Which side of the fee-schedule-category boundary this template's categories
 * came from. This is not a formatting preference: the two sides are different
 * kinds of thing, and the server models them as such.
 *
 * - `"catalog"`: every category is a persisted catalog entry
 *   (`CatalogCategory` server-side) — a legal-entity-scoped, soft-deletable
 *   entity with a stable identity. Its `id` is a real foreign key, so the
 *   canonical `categoryIds` reference payload is sent.
 * - `"values"`: the categories are snapshots
 *   (`FeeScheduleCategorySnapshot` server-side — an owned value object whose
 *   identity is, per its own doc comment, "deliberately distinct from any
 *   canonical catalog category identity"). Their `id` is a client-minted local
 *   handle with no catalog row behind it, so the `categories` value payload is
 *   sent and the server promotes each one into the catalog by normalized name.
 *
 * There is deliberately no default. A category object carries no marker saying
 * which side it is from — `FeeScheduleCategory.id` is always populated and is
 * even minted during deserialization when the server sends none
 * (`fee-schedule-category-schema.ts`), so the source cannot be detected at
 * runtime and must be declared by the caller that knows the provenance.
 * Guessing it wrong is what produced the 400 fixed in commit 11c3d58f3.
 */
export type FeeScheduleTemplateCategorySource = "catalog" | "values";

export type FeeScheduleTemplateCommandOptions = {
  /** Required: see {@link FeeScheduleTemplateCategorySource}. */
  categorySource: FeeScheduleTemplateCategorySource;
  creatorType?: string;
};

/**
 * One catalog category on a template, and who the template means its rate for.
 *
 * The person cannot ride on the category: a catalog category is unique per owner and normalized
 * name, so one "Partner hour" row serves every template the firm has. The server stores this pair
 * on the template's own association row instead.
 */
export type FeeScheduleTemplateCategoryAssignmentPayload = {
  categoryId: string;
  teamMember?: EntityMemberJSON | null;
};

export type FeeScheduleTemplateCommandPayload = {
  entityId: string;
  name: string;
  tags: string[];
  categorySource: FeeScheduleTemplateCategorySource;
  creatorType?: string;
  categoryIds?: string[];
  categoryAssignments?: FeeScheduleTemplateCategoryAssignmentPayload[];
  categories?: FeeScheduleCategoryCommandPayload[];
};

export default class FeeScheduleTemplateCommand {
  public entityId: string;
  public name: string;
  public tags: string[] = [];
  public categoryAssignments: FeeScheduleTemplateCategoryAssignmentPayload[] = [];
  public categories: FeeScheduleCategoryCommandPayload[] = [];
  public creatorType?: string;
  public categorySource: FeeScheduleTemplateCategorySource;

  public get payload(): FeeScheduleTemplateCommandPayload {
    const base = {
      entityId: this.entityId,
      name: this.name,
      tags: this.tags,
      categorySource: this.categorySource,
      creatorType: this.creatorType,
    };

    // The server reads exactly one category payload, in a fixed order of preference, so the two
    // are sent mutually exclusively rather than merged. Sending both would silently select the
    // catalog branch and reproduce the 400 from commit 11c3d58f3.
    //
    // On the `"values"` branch the server keys purely on the normalized category
    // name and mints its own catalog ids, so the client-minted id each
    // FeeScheduleCategoryCommand still carries is ignored rather than trusted. The team member on
    // it is not ignored: that branch pairs the staffing by position.
    return this.categorySource === "values"
      ? { ...base, categories: this.categories }
      : { ...base, categoryAssignments: this.categoryAssignments };
  }

  constructor(template: FeeScheduleTemplate, options: FeeScheduleTemplateCommandOptions) {
    const { categorySource, creatorType = "client" } = options;
    if (!template.creator?.legalEntityId) {
      throw new Error(
        "Template must indicate entity fee schedule is being created for."
      );
    }
    this.entityId = template.creator.legalEntityId.value;
    this.name = template.name;
    this.tags = template.tags.map((tag) => tag.value);
    this.categorySource = categorySource;
    this.categoryAssignments = template.categories.map((category) => ({
      categoryId: category.id.toString(),
      // A member this reader may not see cannot be carried, because the reader was never sent it.
      // Such a rate names nobody on the template, which is what every rate did before templates
      // could name anyone. A template of its own is never masked this way; this guards the one
      // path that promotes a proposal's categories into a template.
      teamMember: category.teamMemberIsMasked ? null : category.teamMember?.toJSON() ?? null,
    }));
    this.categories = template.categories.map(
      (category) => new FeeScheduleCategoryCommand(category).payload,
    );
    this.creatorType = creatorType;
  }
}
